import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Snowflake } from "lucide-react";
import { OCEAN_NAV_LINKS, OCEAN_PATCH_URL } from "../../data/oceanContent";

export default function OceanNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const scroll = () => {
      const el = document.querySelector(href);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    if (location.pathname !== "/ocean") {
      navigate("/ocean");
      setTimeout(scroll, 180);
    } else {
      scroll();
    }
  };

  return (
    <header
      data-testid="ocean-nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-onavy/90 backdrop-blur-md border-oreef/15" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 h-[68px] flex items-center justify-between">
        <Link to="/ocean" onClick={(e) => goTo(e, "#home")} className="flex items-center gap-3 group" data-testid="ocean-nav-brand">
          <img src={OCEAN_PATCH_URL} alt="Arjuna Indian Ocean Expedition mission patch" className="w-9 h-9 rounded-full ring-1 ring-ogold/50 object-cover" />
          <div className="leading-none">
            <span className="font-display font-bold tracking-wide text-oseaglass text-lg block">ARJUNA</span>
            <span className="font-mono text-[9px] tracking-[0.25em] text-oreef block">INDIAN OCEAN · PROPOSED</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {OCEAN_NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => goTo(e, l.href)}
              data-testid={`ocean-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono text-[11px] uppercase tracking-[0.16em] text-oslate hover:text-oreef transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <Link to="/ocean/updates" data-testid="ocean-nav-updates-link" className="font-mono text-[11px] uppercase tracking-[0.16em] text-oslate hover:text-oreef transition-colors duration-300">
            Journal
          </Link>
          <Link to="/" data-testid="ocean-nav-antarctica-link" className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-oslate hover:text-polar transition-colors duration-300">
            <Snowflake size={12} /> Antarctica
          </Link>
          <a
            href="#partners"
            onClick={(e) => goTo(e, "#partners")}
            data-testid="ocean-nav-partner-cta"
            className="font-mono text-[11px] uppercase tracking-[0.16em] px-4 py-2 bg-ocoral text-onavy font-semibold hover:bg-oseaglass transition-colors duration-300 clip-corner"
          >
            Partner With Us
          </a>
        </nav>

        <button
          className="lg:hidden text-oseaglass p-2"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="ocean-nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-onavy border-b border-oreef/15"
            aria-label="Mobile"
            data-testid="ocean-nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {OCEAN_NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => goTo(e, l.href)}
                  data-testid={`ocean-nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-2xl font-semibold text-oseaglass hover:text-oreef transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <Link to="/ocean/updates" onClick={() => setOpen(false)} className="font-display text-2xl font-semibold text-oseaglass hover:text-oreef transition-colors">
                Field Journal
              </Link>
              <Link to="/" onClick={() => setOpen(false)} className="font-display text-2xl font-semibold text-oseaglass hover:text-polar transition-colors">
                Antarctica Mission
              </Link>
              <a href="#partners" onClick={(e) => goTo(e, "#partners")} className="mt-2 font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 bg-ocoral text-onavy font-semibold text-center clip-corner">
                Partner With Us
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
